import { createSlice, type PayloadAction } from '@reduxjs/toolkit';

/* -------------------------------------------------------------------------- */
/* Types                                                                      */
/* -------------------------------------------------------------------------- */

export type TInsurancePurchaseStep = "ownerInfo" | "address" | "summary";

/* ------------------------------ Step 1: Owner / Insurer Info ------------------------------ */

export interface IPersonInfo {
    name: string;
    lastName: string;
    birthDate: string;
    gender: string;
    nationalCode: string;
}

export interface IOwnerInfoStep {
    owner: IPersonInfo;
    // چک‌باکس "بیمه نامه به نام فرد دیگری صادر شود"
    isIssuedToAnotherPerson: boolean;
    // فعال فقط وقتی isIssuedToAnotherPerson=true
    insurer: IPersonInfo;
}

/* ------------------------------ Step 2: Address ------------------------------ */

export interface IAddressStep {
    province: string;
    city: string;
    address: string; // آدرس دقیق
    postalCode: string;
}

/* ------------------------------ Step 3: Summary & Uploads ------------------------------ */

export interface ISummaryStep {
    carCardBackImage: string;   // عکس پشت کارت ماشین
    carCardFrontImage: string;  // عکس روی کارت ماشین
    previousInsuranceImage: string; // عکس بیمه نامه قبلی (اختیاری)
    description: string;       // توضیح (اختیاری)
}

/* ------------------------------- Root State -------------------------------- */

export interface IInsurancePurchaseState {
    step: TInsurancePurchaseStep;
    ownerInfoStep: IOwnerInfoStep;
    addressStep: IAddressStep;
    summaryStep: ISummaryStep;
}

/* -------------------------------------------------------------------------- */
/* Initial State                                                              */
/* -------------------------------------------------------------------------- */

const emptyPersonInfo: IPersonInfo = {
    name: "",
    lastName: "",
    birthDate: "",
    gender: "",
    nationalCode: ""
};

const initialState: IInsurancePurchaseState = {
    step: "summary",
    ownerInfoStep: {
        owner: { ...emptyPersonInfo },
        isIssuedToAnotherPerson: false,
        insurer: { ...emptyPersonInfo }
    },
    addressStep: {
        province: "",
        city: "",
        address: "",
        postalCode: ""
    },
    summaryStep: {
        carCardBackImage: "",
        carCardFrontImage: "",
        previousInsuranceImage: "",
        description: ""
    }
};

/* -------------------------------------------------------------------------- */
/* Slice                                                                      */
/* -------------------------------------------------------------------------- */

const ThirsPartyInsuranceDetail = createSlice({
    name: "insurancePurchase",
    initialState,
    reducers: {
        // ----- Navigation -----
        setStep: (state, action: PayloadAction<TInsurancePurchaseStep>) => {
            state.step = action.payload;
        },

        // ----- Step 1: Owner Info -----
        setOwnerInfo: (state, action: PayloadAction<Partial<IPersonInfo>>) => {
            state.ownerInfoStep.owner = { ...state.ownerInfoStep.owner, ...action.payload };
        },
        setIsIssuedToAnotherPerson: (state, action: PayloadAction<boolean>) => {
            state.ownerInfoStep.isIssuedToAnotherPerson = action.payload;
            // اگر چک‌باکس غیرفعال شد، مشخصات بیمه‌گذار پاک بشه
            if (!action.payload) {
                state.ownerInfoStep.insurer = { ...emptyPersonInfo };
            }
        },
        setInsurerInfo: (state, action: PayloadAction<Partial<IPersonInfo>>) => {
            state.ownerInfoStep.insurer = { ...state.ownerInfoStep.insurer, ...action.payload };
        },

        // ----- Step 2: Address -----
        setAddress: (state, action: PayloadAction<Partial<IAddressStep>>) => {
            state.addressStep = { ...state.addressStep, ...action.payload };
        },

        // ----- Step 3: Summary & Uploads -----
        setSummary: (state, action: PayloadAction<Partial<ISummaryStep>>) => {
            state.summaryStep = { ...state.summaryStep, ...action.payload };
        },

        // ----- Reset -----
        resetInsurancePurchaseForm: () => initialState
    }
});

export const {
    setStep,
    setOwnerInfo,
    setIsIssuedToAnotherPerson,
    setInsurerInfo,
    setAddress,
    setSummary,
    resetInsurancePurchaseForm
} = ThirsPartyInsuranceDetail.actions;

export default ThirsPartyInsuranceDetail.reducer;